// External Intake & Collection v1.0 PR 2 — System-Admin form builder.
// Produces the canonical FormSchema JSON consumed by the (PR-4) party-
// side renderer and the (PR-2) server-side validator. The builder edits
// FormField rows in place, supports add / remove / reorder, exposes
// per-field behaviors (required, default, prefill, validation, help,
// conditional visibility), and lets the admin author repeat groups and
// structural elements (section heading / instruction / page break).

import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button, Input, Textarea, Card, CardContent } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import type {
  FilledBy,
  FormField,
  FormFieldType,
  FormSchema,
  SenderValueSource,
  VisibleWhen,
} from "@/lib/formSchema";

interface CollectionFormBuilderProps {
  schema: FormSchema;
  onChange: (next: FormSchema) => void;
}

const FIELD_TYPE_OPTIONS: ReadonlyArray<{ value: FormFieldType; label: string; group: string }> = [
  { value: "text", label: "Text (short)", group: "Data" },
  { value: "textarea", label: "Text (long)", group: "Data" },
  { value: "number", label: "Number", group: "Data" },
  { value: "currency", label: "Currency", group: "Data" },
  { value: "percentage", label: "Percentage", group: "Data" },
  { value: "date", label: "Date", group: "Data" },
  { value: "email", label: "Email", group: "Data" },
  { value: "phone", label: "Phone", group: "Data" },
  { value: "ssn", label: "SSN (masked)", group: "Data" },
  { value: "ein", label: "EIN (masked)", group: "Data" },
  { value: "dropdown", label: "Dropdown", group: "Choice" },
  { value: "radio", label: "Radio (single)", group: "Choice" },
  { value: "checkbox", label: "Checkbox (boolean)", group: "Choice" },
  { value: "multi-select", label: "Multi-select", group: "Choice" },
  { value: "yesno", label: "Yes / No", group: "Choice" },
  { value: "address", label: "Address block", group: "Composite" },
  { value: "fileattach", label: "File attachment", group: "Composite" },
  { value: "signature", label: "Signature (in-house)", group: "Composite" },
  { value: "initials", label: "Initials", group: "Composite" },
  { value: "datesigned", label: "Date signed (auto)", group: "Composite" },
  { value: "sectionheading", label: "Section heading", group: "Structural" },
  { value: "instruction", label: "Instruction text", group: "Structural" },
  { value: "pagebreak", label: "Page break", group: "Structural" },
  { value: "repeatgroup", label: "Repeat group (add another)", group: "Structural" },
];

const CHOICE_TYPES = new Set<FormFieldType>(["dropdown", "radio", "multi-select"]);
const STRUCTURAL_TYPES = new Set<FormFieldType>(["sectionheading", "instruction", "pagebreak"]);

// Lowercase, ASCII letters/digits/underscore, collapses runs of separators
// into a single underscore. Matches the CollectionFormSchemaValidator
// uniqueness rule (case-insensitive) and the storage convention used by
// existing collection templates.
function slugifyKey(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function defaultFieldFor(type: FormFieldType, index: number): FormField {
  if (STRUCTURAL_TYPES.has(type)) {
    const synthetic = `__struct_${type}_${Date.now()}_${index}`;
    if (type === "sectionheading") {
      return { name: synthetic, label: "New section", type };
    }
    if (type === "instruction") {
      return { name: synthetic, label: "Instruction", type, text: "Write the instruction here." };
    }
    return { name: synthetic, label: "Page break", type };
  }
  if (type === "repeatgroup") {
    return {
      name: `repeat_${index + 1}`,
      label: "Repeat group",
      type,
      addAnotherLabel: "Add another",
      fields: [{ name: "label", label: "Label", type: "text" }],
    };
  }
  if (CHOICE_TYPES.has(type)) {
    return {
      name: `field_${index + 1}`,
      label: "New field",
      type,
      options: [
        { value: "option-1", label: "Option 1" },
        { value: "option-2", label: "Option 2" },
      ],
    };
  }
  return { name: `field_${index + 1}`, label: "New field", type };
}

export function CollectionFormBuilder({ schema, onChange }: CollectionFormBuilderProps) {
  function patchFields(next: FormField[]) {
    onChange({ ...schema, fields: next });
  }

  function addField(type: FormFieldType) {
    patchFields([...schema.fields, defaultFieldFor(type, schema.fields.length)]);
  }

  function updateField(index: number, patch: Partial<FormField>) {
    const next = schema.fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
    patchFields(next);
  }

  function removeField(index: number) {
    patchFields(schema.fields.filter((_, i) => i !== index));
  }

  function moveField(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= schema.fields.length) return;
    const next = [...schema.fields];
    [next[index], next[target]] = [next[target], next[index]];
    patchFields(next);
  }

  return (
    <div className="flex flex-col gap-space-3" data-testid="collection-form-builder">
      <header className="flex flex-wrap items-center gap-space-2">
        <span className="text-small font-medium text-onyx">Add field:</span>
        <AddFieldDropdown onAdd={addField} />
      </header>

      {schema.fields.length === 0 ? (
        <p className="text-small text-gray-mid italic">
          No fields yet. Add a field above to start authoring the form.
        </p>
      ) : (
        <ol className="flex flex-col gap-space-3 list-none p-0">
          {schema.fields.map((field, index) => (
            <li key={field.name} data-testid={`field-row-${index}`}>
              <FieldRow
                field={field}
                index={index}
                total={schema.fields.length}
                allFields={schema.fields}
                onUpdate={(patch) => updateField(index, patch)}
                onRemove={() => removeField(index)}
                onMove={(dir) => moveField(index, dir)}
              />
            </li>
          ))}
        </ol>
      )}

      {schema.fields.length > 0 && (
        <footer className="flex flex-wrap items-center gap-space-2">
          <span className="text-small font-medium text-onyx">Add field:</span>
          <AddFieldDropdown onAdd={addField} />
        </footer>
      )}
    </div>
  );
}

function AddFieldDropdown({ onAdd }: { onAdd: (type: FormFieldType) => void }) {
  return (
    <Select
      aria-label="Add field type"
      value=""
      onChange={(e) => {
        const v = e.target.value as FormFieldType;
        if (v) onAdd(v);
        e.target.value = "";
      }}
    >
      <option value="">Choose a field type…</option>
      {Array.from(new Set(FIELD_TYPE_OPTIONS.map((o) => o.group))).map((group) => (
        <optgroup key={group} label={group}>
          {FIELD_TYPE_OPTIONS.filter((o) => o.group === group).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  );
}

interface FieldRowProps {
  field: FormField;
  index: number;
  total: number;
  allFields: FormField[];
  onUpdate: (patch: Partial<FormField>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}

function FieldRow({ field, index, total, allFields, onUpdate, onRemove, onMove }: FieldRowProps) {
  const isStructural = STRUCTURAL_TYPES.has(field.type);
  const isChoice = CHOICE_TYPES.has(field.type);
  const isRepeatGroup = field.type === "repeatgroup";

  // Storage key auto-derives from the label unless the author has typed a
  // custom value. Stays editable; once edited the link breaks.
  const [nameTouched, setNameTouched] = useState(false);

  const visibilityCandidates = useMemo(
    () => allFields.filter((f) => f.name !== field.name && !STRUCTURAL_TYPES.has(f.type)),
    [allFields, field.name],
  );

  function handleLabelChange(nextLabel: string) {
    if (!isStructural && !nameTouched) {
      const derived = slugifyKey(nextLabel);
      onUpdate({ label: nextLabel, name: derived || field.name });
    } else {
      onUpdate({ label: nextLabel });
    }
  }

  function handleNameChange(nextName: string) {
    setNameTouched(true);
    onUpdate({ name: nextName });
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-space-3 p-space-3">
        <div className="flex items-center justify-between gap-space-2">
          <span className="text-caption uppercase tracking-wide text-gray-mid">
            #{index + 1} · {field.type}
          </span>
          <div className="flex items-center gap-space-1">
            <Button
              variant="secondary"
              size="sm"
              type="button"
              aria-label="Move up"
              onClick={() => onMove(-1)}
              disabled={index === 0}
            >
              <ArrowUp size={14} />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              aria-label="Move down"
              onClick={() => onMove(1)}
              disabled={index === total - 1}
            >
              <ArrowDown size={14} />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              type="button"
              aria-label="Remove field"
              onClick={onRemove}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

        {field.type === "pagebreak" ? (
          <p className="text-small text-gray-mid italic">
            Page break — the renderer paginates here.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
              <LabeledInput
                label={isStructural ? "Display text" : "Label"}
                value={field.label}
                onChange={(v) => isStructural ? onUpdate({ label: v }) : handleLabelChange(v)}
                required
              />
              {!isStructural && (
                <LabeledInput
                  label="Field name (storage key)"
                  value={field.name}
                  onChange={handleNameChange}
                  required
                  helpText="Lowercase letters, digits, and underscores. Auto-filled from the label; edit to customize."
                />
              )}
            </div>

            {field.type === "instruction" && (
              <LabeledTextarea
                label="Instruction body"
                value={field.text ?? ""}
                onChange={(v) => onUpdate({ text: v })}
                rows={3}
              />
            )}

            {!isStructural && (
              <div className="flex flex-wrap items-center gap-space-3">
                <label className="flex items-center gap-space-2 text-small text-onyx">
                  <input
                    type="checkbox"
                    checked={!!field.required}
                    onChange={(e) => onUpdate({ required: e.target.checked })}
                  />
                  Required
                </label>
                <LabeledInput
                  label="Help text"
                  className="flex-1 min-w-[200px]"
                  value={field.helpText ?? ""}
                  onChange={(v) => onUpdate({ helpText: v || undefined })}
                />
              </div>
            )}

            {!isStructural && !isRepeatGroup && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
                <LabeledInput
                  label="Default value"
                  value={
                    field.defaultValue === null || field.defaultValue === undefined
                      ? ""
                      : String(field.defaultValue)
                  }
                  onChange={(v) => onUpdate({ defaultValue: v || undefined })}
                />
                <LabeledInput
                  label="Prefill from (e.g. recipient.fullName)"
                  value={field.prefillFrom ?? ""}
                  onChange={(v) => onUpdate({ prefillFrom: v || undefined })}
                />
              </div>
            )}

            {!isStructural && !isRepeatGroup && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
                <label className="flex flex-col gap-space-1 text-small text-onyx">
                  <span>Filled by</span>
                  <Select
                    aria-label="Filled by"
                    value={field.filledBy ?? "Recipient"}
                    onChange={(e) => {
                      const v = e.target.value as FilledBy;
                      onUpdate(
                        v === "Sender"
                          ? { filledBy: "Sender", valueSource: field.valueSource ?? "Manual" }
                          : { filledBy: undefined, valueSource: undefined, sourceToken: undefined },
                      );
                    }}
                  >
                    <option value="Recipient">Recipient</option>
                    <option value="Sender">Sender (staff)</option>
                  </Select>
                </label>
                {field.filledBy === "Sender" && (
                  <label className="flex flex-col gap-space-1 text-small text-onyx">
                    <span>Value source</span>
                    <Select
                      aria-label="Sender value source"
                      value={field.valueSource ?? "Manual"}
                      onChange={(e) => {
                        const v = e.target.value as SenderValueSource;
                        onUpdate({
                          valueSource: v,
                          sourceToken: v === "DealField" ? field.sourceToken : undefined,
                        });
                      }}
                    >
                      <option value="Manual">Manual (typed at send)</option>
                      <option value="DealField">Deal field</option>
                      <option value="Computed">Computed</option>
                      <option value="System">System</option>
                    </Select>
                  </label>
                )}
                {field.filledBy === "Sender" && field.valueSource === "DealField" && (
                  <LabeledInput
                    label="Source token (e.g. deal.purchasePrice)"
                    value={field.sourceToken ?? ""}
                    onChange={(v) => onUpdate({ sourceToken: v || undefined })}
                  />
                )}
              </div>
            )}

            {isChoice && (
              <OptionsEditor
                options={field.options ?? []}
                onChange={(options) => onUpdate({ options })}
              />
            )}

            {isRepeatGroup && (
              <RepeatGroupEditor
                fields={field.fields ?? []}
                addAnotherLabel={field.addAnotherLabel ?? "Add another"}
                onChange={(patch) => onUpdate(patch)}
              />
            )}

            {!isStructural && (
              <VisibilityEditor
                visibleWhen={field.visibleWhen}
                candidates={visibilityCandidates}
                onChange={(visibleWhen) => onUpdate({ visibleWhen })}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  required,
  className,
  helpText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  className?: string;
  helpText?: string;
}) {
  return (
    <label className={`flex flex-col gap-space-1 text-small text-onyx ${className ?? ""}`}>
      <span>
        {label}
        {required && <span className="text-error ml-space-1">*</span>}
      </span>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
      {helpText && <span className="text-caption text-gray-mid">{helpText}</span>}
    </label>
  );
}

function LabeledTextarea({
  label,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="flex flex-col gap-space-1 text-small text-onyx">
      <span>{label}</span>
      <Textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function OptionsEditor({
  options,
  onChange,
}: {
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (next: Array<{ value: string; label: string }>) => void;
}) {
  function update(i: number, patch: Partial<{ value: string; label: string }>) {
    onChange(options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  }
  function add() {
    onChange([
      ...options,
      { value: `option-${options.length + 1}`, label: `Option ${options.length + 1}` },
    ]);
  }
  function remove(i: number) {
    onChange(options.filter((_, idx) => idx !== i));
  }

  return (
    <div className="flex flex-col gap-space-2">
      <span className="text-small font-medium text-onyx">Options</span>
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-space-2">
          <Input
            placeholder="value"
            value={opt.value}
            onChange={(e) => update(i, { value: e.target.value })}
          />
          <Input
            placeholder="label"
            value={opt.label}
            onChange={(e) => update(i, { label: e.target.value })}
          />
          <Button
            variant="destructive"
            size="sm"
            type="button"
            aria-label={`Remove option ${i + 1}`}
            onClick={() => remove(i)}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
      <Button variant="secondary" size="sm" type="button" onClick={add}>
        <Plus size={14} className="mr-space-1" /> Add option
      </Button>
    </div>
  );
}

function RepeatGroupEditor({
  fields,
  addAnotherLabel,
  onChange,
}: {
  fields: FormField[];
  addAnotherLabel: string;
  onChange: (patch: Partial<FormField>) => void;
}) {
  function patchFields(next: FormField[]) {
    onChange({ fields: next });
  }
  function addNested(type: FormFieldType) {
    patchFields([...fields, defaultFieldFor(type, fields.length)]);
  }
  function updateNested(i: number, patch: Partial<FormField>) {
    patchFields(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }
  function removeNested(i: number) {
    patchFields(fields.filter((_, idx) => idx !== i));
  }

  return (
    <div className="flex flex-col gap-space-2 border-l-2 border-gold-light pl-space-3">
      <LabeledInput
        label='"Add another" button label'
        value={addAnotherLabel}
        onChange={(v) => onChange({ addAnotherLabel: v || "Add another" })}
      />
      <span className="text-small font-medium text-onyx">Nested fields</span>
      {fields.length === 0 ? (
        <p className="text-caption text-gray-mid italic">No nested fields yet.</p>
      ) : (
        <ul className="flex flex-col gap-space-2 list-none p-0">
          {fields.map((f, i) => (
            <li key={f.name} className="flex items-center gap-space-2">
              <Input
                aria-label={`Nested field ${i + 1} name`}
                value={f.name}
                onChange={(e) => updateNested(i, { name: e.target.value })}
              />
              <Input
                aria-label={`Nested field ${i + 1} label`}
                value={f.label}
                onChange={(e) => updateNested(i, { label: e.target.value })}
              />
              <Select
                aria-label={`Nested field ${i + 1} type`}
                value={f.type}
                onChange={(e) => updateNested(i, { type: e.target.value as FormFieldType })}
              >
                {FIELD_TYPE_OPTIONS.filter(
                  (o) => o.group === "Data" || o.group === "Choice",
                ).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <Button
                variant="destructive"
                size="sm"
                type="button"
                aria-label={`Remove nested field ${i + 1}`}
                onClick={() => removeNested(i)}
              >
                <Trash2 size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Select
          aria-label="Add nested field"
          value=""
          onChange={(e) => {
            const v = e.target.value as FormFieldType;
            if (v) addNested(v);
            e.target.value = "";
          }}
        >
          <option value="">Add nested field…</option>
          {FIELD_TYPE_OPTIONS.filter((o) => o.group === "Data" || o.group === "Choice").map(
            (o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ),
          )}
        </Select>
      </div>
    </div>
  );
}

function VisibilityEditor({
  visibleWhen,
  candidates,
  onChange,
}: {
  visibleWhen?: VisibleWhen;
  candidates: FormField[];
  onChange: (next: VisibleWhen | undefined) => void;
}) {
  const enabled = !!visibleWhen;

  return (
    <div className="flex flex-col gap-space-2">
      <label className="flex items-center gap-space-2 text-small text-onyx">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            if (e.target.checked) {
              const first = candidates[0];
              if (first) onChange({ field: first.name, equals: "" });
            } else {
              onChange(undefined);
            }
          }}
          disabled={candidates.length === 0}
        />
        Show only when another field equals a specific value
      </label>
      {enabled && visibleWhen && (
        <div className="flex flex-wrap items-center gap-space-2 pl-space-4">
          <Select
            aria-label="Visibility dependency field"
            value={visibleWhen.field}
            onChange={(e) => onChange({ ...visibleWhen, field: e.target.value })}
          >
            {candidates.map((c) => (
              <option key={c.name} value={c.name}>
                {c.label} ({c.name})
              </option>
            ))}
          </Select>
          <span className="text-small text-gray-mid">equals</span>
          <Input
            aria-label="Visibility equals value"
            value={
              visibleWhen.equals === null || visibleWhen.equals === undefined
                ? ""
                : String(visibleWhen.equals)
            }
            onChange={(e) => onChange({ field: visibleWhen.field, equals: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}
